/**
 * Playback drop-off analytics — 25 / 50 / 75 / 100 % milestones.
 *
 * Each milestone fires exactly once per tracker instance. The page emits
 * `play_progress` signals via {@link window.XOT.beacon}; the tracker is
 * a thin policy layer on top: which-milestone-crossed bookkeeping +
 * idempotency, no transport logic of its own.
 *
 * Factory pattern: `createDropoffTracker(opts)` returns a fresh stateful
 * tracker. A new tracker is minted for each playback generation (so a
 * user who replays the same article gets a clean milestone set).
 *
 * The 25/50/75 thresholds fire from time-update probes. The 100%
 * milestone has no natural threshold (audio simply ends), so it's a
 * separate `complete()` method invoked from the `ended` event.
 *
 * Build path:
 *   src   assets/js/src/single-article/dropoff.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/dropoff.js  (attaches window.XOT.article.dropoff)
 *
 * @since Phase 2 — JS modernization, bite 10
 */

const DEFAULT_SECTION = 'tts';

/** Snapshot of which thresholds have already fired for this instance. */
export interface DropoffState {
    readonly p25:  boolean;
    readonly p50:  boolean;
    readonly p75:  boolean;
    readonly p100: boolean;
}

export interface DropoffTracker {
    /** Read the current per-milestone fired state. */
    state(): DropoffState;
    /** Reset every milestone — call before a fresh playback generation. */
    reset(): void;
    /** Threshold-cross check: fires p25/p50/p75 beacons as crossed. No-op for 100%. */
    check(currentTime: number, duration: number): void;
    /** Fire the 100% milestone — called from the audio `ended` event. */
    complete(): void;
}

export interface DropoffOptions {
    /** Override the beacon endpoint. Defaults to the xot-beacon default. */
    endpoint?: string;
    /** WP REST nonce passed through to xot-beacon. */
    nonce?: string;
    /** Entity ID (post_id) associated with the playback. */
    entity?: string | number | null;
    /** Beacon `section` field. Defaults to "tts". */
    section?: string;
}

interface BeaconSurface {
    sendOne: (event: object, opts?: { endpoint?: string; nonce?: string }) => boolean;
}

/**
 * Build a stateful dropoff tracker. Each tracker carries its own
 * milestone-fired flags and beacon configuration — minimal coupling to
 * any page-level state.
 */
export function createDropoffTracker(opts: DropoffOptions = {}): DropoffTracker {
    const section  = opts.section ?? DEFAULT_SECTION;
    const entity   = opts.entity  ?? null;
    const beaconOpts = { endpoint: opts.endpoint, nonce: opts.nonce };

    const marks = { p25: false, p50: false, p75: false, p100: false };

    function getBeacon(): BeaconSurface | null {
        return (typeof window !== 'undefined' && window.XOT && (window.XOT as { beacon?: BeaconSurface }).beacon) || null;
    }

    function fire(pct: 25 | 50 | 75 | 100): void {
        const b = getBeacon();
        if (!b) return;
        b.sendOne(
            { type: 'play_progress', section, item: 'p' + pct, value: pct, entity },
            beaconOpts,
        );
    }

    return {
        state(): DropoffState {
            return { p25: marks.p25, p50: marks.p50, p75: marks.p75, p100: marks.p100 };
        },
        reset(): void {
            marks.p25  = false;
            marks.p50  = false;
            marks.p75  = false;
            marks.p100 = false;
        },
        check(currentTime: number, duration: number): void {
            if (!duration || duration <= 0) return;
            const pct = currentTime / duration;
            if (pct >= 0.25 && !marks.p25) { marks.p25 = true; fire(25); }
            if (pct >= 0.50 && !marks.p50) { marks.p50 = true; fire(50); }
            if (pct >= 0.75 && !marks.p75) { marks.p75 = true; fire(75); }
        },
        complete(): void {
            if (marks.p100) return;
            marks.p100 = true;
            fire(100);
        },
    };
}

/* ── window.XOT.article.dropoff attachment ─────────────────────────── */
interface XotArticleDropoff {
    create: typeof createDropoffTracker;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:         unknown;
                highlight?:    unknown;
                progress?:     unknown;
                share?:        unknown;
                audio?:        unknown;
                mediaSession?: unknown;
                dropoff?:      XotArticleDropoff;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.dropoff = { create: createDropoffTracker };
    xot.article = article;
    window.XOT  = xot;
}
